// Xelsed AI Provider - Fine-tuned Gemini for p5.js
// Uses Vertex AI tuned model endpoint with service account authentication
// Model: p5flash-alpha02 (xelsed-p5flash-alpha02 - best performer)

import { getOrCreateDeviceId, getSessionId } from '../utils'

let currentAbortController = null


/**
 * Abort current Xelsed request
 */
export function abortXelsed() {
  if (currentAbortController) {
    currentAbortController.abort()
    currentAbortController = null
  }
}

// Alias for consistency with other providers
export const stopXelsed = abortXelsed

/**
 * Main Xelsed call function (Vertex AI Tuned Endpoint)
 * @param {Array} messages - Messages with role and content
 * @param {string|null} userApiKey - Not used (server-only)
 * @param {string} model - Model name (alpha01-test)
 * @param {function} onDelta - Callback for real-time content streaming
 * @param {function} onThinking - Callback for real-time thinking (not used for tuned model)
 * @returns {Promise<{content: string, reasoning: string, usage: object}>}
 */
export async function callXelsed(messages, userApiKey, model = 'p5flash-alpha02', onDelta = null, onThinking = null) {
  currentAbortController = new AbortController()
  
  console.log('🟣 Xelsed (Tuned Gemini):', { model })
  
  // Always use server - tuned model requires service account auth
  return await callThroughServer(messages, model, onDelta, onThinking)
}

// Server streaming (through Cloudflare function)
async function callThroughServer(messages, model, onDelta, onThinking) {
  // Get device ID for job persistence
  const deviceId = getOrCreateDeviceId()
  const sessionId = getSessionId()
  
  try {
    const response = await fetch('/api/ai-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Connection': 'keep-alive'
      },
      body: JSON.stringify({
        messages,
        provider: 'xelsed',
        model,
        // Job persistence fields
        deviceId,
        sessionId
      }),
      signal: currentAbortController.signal,
      cache: 'no-store'
    })
    
    return await handleStreamingResponse(response, onDelta, onThinking)
  } catch (error) {
    console.error('Xelsed server error:', error)
    throw error
  }
}

// Handle streaming response from server
async function handleStreamingResponse(response, onDelta, onThinking) {
  console.log('🟣 Xelsed streaming response:', { 
    ok: response.ok, 
    status: response.status,
    contentType: response.headers.get('content-type')
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('❌ Xelsed server error:', errorText)
    const error = response.status === 504 
      ? '⏱️ Response timed out. The tuned model may be loading.'
      : errorText || 'Xelsed request failed'
    throw new Error(error)
  }
  
  if (!response.body) {
    throw new Error('Streaming not supported')
  }

  // Parse SSE stream
  let content = ''
  let reasoning = ''
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let chunkCount = 0

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        console.log(`✅ Stream complete. Received ${chunkCount} chunks, ${content.length} chars`)
        break
      }

      chunkCount++
      const chunkText = decoder.decode(value, { stream: true })
      console.log(`🔍 Chunk ${chunkCount}:`, chunkText.substring(0, 200))
      buffer += chunkText
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      
      for (const line of lines) {
        if (!line.trim() || !line.startsWith('data: ')) continue
        
        const dataStr = line.slice(6)
        if (dataStr === '[DONE]') continue

        try {
          const data = JSON.parse(dataStr)
          
          if (data.type === 'delta' && data.text) {
            content += data.text
            if (onDelta) onDelta(data.text)  // Stream to UI!
          } else if (data.type === 'thinking' && onThinking) {
            const thinkingDelta = data.text || ''
            if (thinkingDelta) {
              console.log('🧠 Thinking delta:', thinkingDelta.substring(0, 50) + '...')
              onThinking(thinkingDelta)
            }
          } else if (data.type === 'error') {
            throw new Error(data.message || 'Stream error')
          }
        } catch (e) {
          console.warn('Parse error:', e, 'Line:', line)
        }
      }
    }
  } catch (error) {
    console.error('❌ Stream error:', error)
    if (content.length > 0) {
      console.log('⚠️  Returning partial content:', content.length, 'chars')
      const normalized = normalizeXelsedContent(content)
      return { content: normalized, reasoning, usage: null }
    }
    throw error
  }

  if (!content) {
    console.error('❌ No content received after', chunkCount, 'chunks')
    throw new Error('No response received from Xelsed. Please try again.')
  }

  const normalizedContent = normalizeXelsedContent(content)
  return { content: normalizedContent, reasoning, usage: null }
}

function normalizeXelsedContent(raw) {
  if (!raw) return raw

  // Fix duplicate jsdelivr in domain (model hallucination)
  raw = raw.replace(/cdn\.jsdelivr\.jsdelivr\.net/g, 'cdn.jsdelivr.net')

  // Replace cdnjs URLs with jsdelivr (safety net for model training bias)
  raw = raw.replace(
    /https:\/\/cdnjs\.cloudflare\.com\/ajax\/libs\/p5\.js\/([^/]+)\/p5\.js/g,
    'https://cdn.jsdelivr.net/npm/p5@$1/lib/p5.js'
  )
  raw = raw.replace(
    /https:\/\/cdnjs\.cloudflare\.com\/ajax\/libs\/p5\.js\/([^/]+)\/addons\/p5\.sound\.min\.js/g,
    'https://cdn.jsdelivr.net/npm/p5@$1/lib/addons/p5.sound.min.js'
  )

  // Convert Google Fonts gstatic URLs to fontsource CDN (more reliable for loadFont)
  // Pattern: https://fonts.gstatic.com/s/fontname/v123/hash.woff2
  // Convert to: https://unpkg.com/@fontsource/font-name@latest/files/font-name-latin-400-normal.woff
  raw = raw.replace(
    /https:\/\/fonts\.gstatic\.com\/s\/([a-z0-9]+)\/v\d+\/[^"'\s)]+\.woff2?/gi,
    (match, fontName) => {
      // Convert camelCase/lowercase to slug: "pressstart2p" -> "press-start-2p"
      const slug = fontName
        .replace(/([a-z])([A-Z0-9])/g, '$1-$2')
        .replace(/([0-9])([a-z])/gi, '$1-$2')
        .toLowerCase()
      return `https://unpkg.com/@fontsource/${slug}@latest/files/${slug}-latin-400-normal.woff`
    }
  )

  // If the model already returned markdown code fences, keep as-is
  if (raw.includes('```')) return raw

  // Look for sections like:
  // // === sketch.js ===\n...code...
  const sectionRegex = /\/\/\s*===\s*([\w.-]+\.[\w]+)\s*===\s*\n([\s\S]*?)(?=(\/\/\s*===\s*[\w.-]+\.[\w]+\s*===)|$)/g
  const sections = []
  let match

  while ((match = sectionRegex.exec(raw)) !== null) {
    const fileName = match[1].trim()
    let code = (match[2] || '').trim()

    // Strip trailing survey text like "Was this helpful?"
    code = code.replace(/Was this helpful\??\s*$/i, '').trim()

    if (code) {
      sections.push({ fileName, code })
    }
  }

  // If we didn't detect any sections, return the original text
  if (!sections.length) return raw

  // Preserve any intro text before the first marker as plain explanation
  const firstMarkerIndex = raw.indexOf('// ===')
  let intro = ''
  if (firstMarkerIndex > 0) {
    intro = raw.slice(0, firstMarkerIndex).trim()
  }

  const parts = []
  if (intro) {
    parts.push(intro)
    parts.push('')
  }

  sections.forEach(({ fileName, code }) => {
    let lang = ''
    if (fileName.endsWith('.js')) lang = 'javascript'
    else if (fileName.endsWith('.html')) lang = 'html'
    else if (fileName.endsWith('.css')) lang = 'css'
    else if (fileName.endsWith('.json')) lang = 'json'

    // AiAssistantSimple's parser supports: ```lang filename\n...code...```
    const header = lang ? `\`\`\`${lang} ${fileName}` : `\`\`\`${fileName}`
    parts.push(header)
    parts.push(code)
    parts.push('\`\`\`')
    parts.push('')
  })

  return parts.join('\n')
}
